// src/lib/firestore.ts
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  updateDoc,
  increment,
} from 'firebase/firestore';
import { db } from './firebase';
import { TestingEntry, UserStats } from '@/types';
import { format } from 'date-fns';
import { loadHolidaysForYears, shouldContinueStreak } from './businessDays';

const COLLECTIONS = {
  USERS: 'users',
  TESTING_ENTRIES: 'testing_entries',
  USER_STATS: 'user_stats',
};

export const createOrUpdateUser = async (
  uid: string,
  email: string,
  displayName: string,
  photoURL: string
) => {
  console.log('👤 createOrUpdateUser called for:', displayName, uid);
  
  const userRef = doc(db, COLLECTIONS.USERS, uid);
  const statsRef = doc(db, COLLECTIONS.USER_STATS, uid);
  
  const [userSnap, statsSnap] = await Promise.all([
    getDoc(userRef),
    getDoc(statsRef),
  ]);

  // Create user document if it doesn't exist
  if (!userSnap.exists()) {
    console.log('🆕 Creating new user document...');
    
    await setDoc(userRef, {
      uid,
      email,
      displayName,
      photoURL,
      createdAt: Timestamp.now(),
    });
  } else {
    console.log('✓ User document already exists');
  }

  // Always ensure stats document exists (even if user exists)
  if (!statsSnap.exists()) {
    console.log('📊 Creating user stats document...');
    
    await setDoc(statsRef, {
      userId: uid,
      userName: displayName,
      userPhoto: photoURL,
      totalDays: 0,
      currentStreak: 0,
      longestStreak: 0,
      lastTestDate: '',
      weeklyTests: 0,
      monthlyTests: 0,
      achievements: [],
    });
    
    console.log('✅ User stats created successfully!');
  } else {
    console.log('✓ User stats already exist');
  }
};

export const recordTestingEntry = async (
  userId: string,
  userName: string,
  userPhoto: string,
  notes?: string
): Promise<void> => {
  const today = format(new Date(), 'yyyy-MM-dd');
  const entryId = `${userId}_${today}`;

  const entryRef = doc(db, COLLECTIONS.TESTING_ENTRIES, entryId);
  const entrySnap = await getDoc(entryRef);

  if (entrySnap.exists()) {
    throw new Error('Already recorded testing for today');
  }

  await setDoc(entryRef, {
    id: entryId,
    userId,
    userName,
    userPhoto,
    date: today,
    timestamp: Timestamp.now(),
    notes: notes || '',
  });

  await updateUserStats(userId, today);
};

const updateUserStats = async (userId: string, testDate: string) => {
  const statsRef = doc(db, COLLECTIONS.USER_STATS, userId);
  const statsSnap = await getDoc(statsRef);

  if (!statsSnap.exists()) return;

  const stats = statsSnap.data() as UserStats;
  const lastDate = stats.lastTestDate;

  let newStreak = 1;
  
  if (lastDate) {
    // Load holidays for the relevant years
    const lastYear = new Date(lastDate).getFullYear();
    const currentYear = new Date(testDate).getFullYear();
    const countryCode = import.meta.env.VITE_COUNTRY_CODE || 'US';
    
    const holidays = await loadHolidaysForYears(lastYear, currentYear, countryCode);
    
    // Check if streak should continue based on business days
    const streakCheck = shouldContinueStreak(lastDate, testDate, holidays);
    
    if (streakCheck.continues && streakCheck.businessDaysDiff === 0) {
      // Same day - keep current streak
      newStreak = stats.currentStreak;
    } else if (streakCheck.continues && streakCheck.businessDaysDiff === 1) {
      // Next working day - increment streak
      newStreak = stats.currentStreak + 1;
      console.log(`🔥 Streak continued: ${lastDate} → ${testDate} (working day)`);
    } else {
      // Missed working day(s) - reset streak
      newStreak = 1;
      console.log(`💔 Streak reset: ${lastDate} → ${testDate} (${streakCheck.businessDaysDiff} business days apart)`);
    }
  } else {
    console.log('🆕 Starting new streak');
  }

  await updateDoc(statsRef, {
    totalDays: increment(1),
    currentStreak: newStreak,
    longestStreak: Math.max(stats.longestStreak, newStreak),
    lastTestDate: testDate,
    weeklyTests: increment(1),
    monthlyTests: increment(1),
  });
};


export const getUserStats = async (userId: string): Promise<UserStats | null> => {
  const statsRef = doc(db, COLLECTIONS.USER_STATS, userId);
  const statsSnap = await getDoc(statsRef);

  console.log('📊 getUserStats for:', userId, 'exists:', statsSnap.exists());

  if (!statsSnap.exists()) {
    console.warn('⚠️ No stats document found for user:', userId);
    return null;
  }

  return statsSnap.data() as UserStats;
};

export const getLeaderboard = async (limitCount = 50) => {
  const q = query(
    collection(db, COLLECTIONS.USER_STATS),
    orderBy('currentStreak', 'desc'),
    orderBy('totalDays', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => doc.data() as UserStats);
};

export const getRecentActivity = async (limitCount = 20) => {
  const q = query(
    collection(db, COLLECTIONS.TESTING_ENTRIES),
    orderBy('timestamp', 'desc'),
    limit(limitCount)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      ...data,
      timestamp: data.timestamp.toDate(),
    } as TestingEntry;
  });
};

export const hasTestedToday = async (userId: string): Promise<boolean> => {
  const today = format(new Date(), 'yyyy-MM-dd');
  const entryId = `${userId}_${today}`;
  const entryRef = doc(db, COLLECTIONS.TESTING_ENTRIES, entryId);
  const entrySnap = await getDoc(entryRef);

  return entrySnap.exists();
};

export const getUserTestingHistory = async (
  userId: string,
  days: number = 84
): Promise<Array<{ date: string; count: number }>> => {
  try {
    console.log('📊 Fetching testing history for user:', userId, 'days:', days);
    
    const result: Array<{ date: string; count: number }> = [];
    const today = new Date();
    
    // Create map of dates to check
    const dateMap: Record<string, number> = {};
    
    // Query all user's testing entries
    const q = query(
      collection(db, COLLECTIONS.TESTING_ENTRIES),
      where('userId', '==', userId)
    );
    
    const snapshot = await getDocs(q);
    
    // Count tests per date
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.date) {
        dateMap[data.date] = (dateMap[data.date] || 0) + 1;
      }
    });
    
    console.log('📅 Found', snapshot.size, 'testing entries');
    
    // Generate array for last N days with counts
    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      
      result.push({
        date: dateStr,
        count: dateMap[dateStr] || 0,
      });
    }
    
    return result;
  } catch (error) {
    console.error('❌ Error fetching testing history:', error);
    // Return empty data on error
    return Array.from({ length: days }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return {
        date: date.toISOString().split('T')[0],
        count: 0,
      };
    });
  }
};

export const getTeamTestingHistory = async (
  days: number = 90
): Promise<Array<{ date: string; count: number; users: number }>> => {
  try {
    console.log('📊 Fetching team testing history for', days, 'days');
    
    const today = new Date();
    const dateMap: Record<string, { count: number; userIds: Set<string> }> = {};
    
    // Query all testing entries
    const q = query(
      collection(db, COLLECTIONS.TESTING_ENTRIES),
      orderBy('date', 'desc')
    );
    
    const snapshot = await getDocs(q);
    
    // Aggregate by date
    snapshot.forEach((doc) => {
      const data = doc.data();
      if (data.date) {
        if (!dateMap[data.date]) {
          dateMap[data.date] = { count: 0, userIds: new Set() };
        }
        dateMap[data.date].count += 1;
        dateMap[data.date].userIds.add(data.userId);
      }
    });
    
    console.log('📅 Found', snapshot.size, 'total testing entries');
    
    // Generate array for last N days
    const result = [];
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayData = dateMap[dateStr];
      
      result.push({
        date: dateStr,
        count: dayData?.count || 0,
        users: dayData?.userIds.size || 0,
      });
    }
    
    return result;
  } catch (error) {
    console.error('❌ Error fetching team history:', error);
    // Return empty data on error
    return Array.from({ length: days }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (days - 1 - i));
      return {
        date: date.toISOString().split('T')[0],
        count: 0,
        users: 0,
      };
    });
  }
};

